/**
 * Staff Partner Detail Page - view partner users and impersonate them.
 *
 * Mirrors the tenant detail page pattern: shows partnership info,
 * lists users with impersonate buttons.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import {
  ArrowLeft,
  ArrowRightLeft,
  Building2,
  Users,
  Handshake,
} from 'lucide-react'
import { toast } from 'sonner'
import api, { setAuthToken, clearActiveOrgContext } from '@/admin/lib/api'
import { useFormatters } from '@/shared/lib/formatting'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
interface PartnerDetail {
  partnership: {
    uuid: string
    upstream_organisation: { uuid: string; name: string }
    downstream_organisation: { uuid: string; name: string; slug: string }
    role_name: string | null
    is_active: boolean
    user_management_strategy: string
    created_at: string | null
  }
  members: Array<{
    uuid: string
    email: string
    first_name: string
    last_name: string
    is_staff: boolean
    role_name: string | null
    role_names?: string[]
    has_partnership_access: boolean
    created_at: string | null
  }>
}

const STRATEGY_LABELS: Record<string, string> = {
  managed: 'Managed by upstream',
  delegated: 'Managed by partner',
  direct_inheritance: 'Auto-inherited',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StaffPartnerDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'error' } | { status: 'loaded'; data: PartnerDetail }
  >({ status: 'loading' })

  useEffect(() => {
    if (!uuid) return
    let cancelled = false
    api
      .get(`/staff/partners/${uuid}/`)
      .then((res) => { if (!cancelled) setState({ status: 'loaded', data: res.data }) })
      .catch(() => { if (!cancelled) setState({ status: 'error' }) })
    return () => { cancelled = true }
  }, [uuid])

  async function handleImpersonate(userUuid: string) {
    try {
      const res = await api.post(`/staff/impersonate/${userUuid}/`)
      if (res.data.session_token) setAuthToken(res.data.session_token)
      // The tab still holds the staff user's active org selection, which would
      // ride along as X-Active-User-Organisation-UUID and strand the
      // impersonated user with no partner context.
      clearActiveOrgContext()
      toast.success(`Now impersonating ${res.data.user.email}`)
      globalThis.location.assign('/dashboard')
    } catch (err: unknown) {
      toastApiError(err, 'Failed to impersonate');
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[var(--bg)]">
        <p className="text-[13px] text-[var(--ink-3)]">Partner not found</p>
        <button
          onClick={() => navigate(-1)}
          className="cursor-pointer text-[11px] underline text-[var(--green)]"
        >
          Go back
        </button>
      </div>
    );
  }

  const { partnership, members } = state.data
  const usersWithAccess = members.filter(m => m.has_partnership_access).length
  const downstream = partnership.downstream_organisation

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header bar */}
      <header
        className="flex h-14 items-center gap-4 border-b-[0.5px] border-b-[var(--rule)] px-6"
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors cursor-pointer hover:bg-[var(--bg-subtle)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="h-4 w-px bg-[var(--rule)]" />
        <span className="clara-section-label tracking-[0.14em]">
          iWarranty
        </span>
        <span
          className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]"
        >
          Staff
        </span>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Title */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
              >
                <Building2 className="h-5 w-5 text-[var(--ink-3)]" />
              </div>
              <div>
                <h1 className="clara-title">{downstream.name}</h1>
                <span className="text-[12px] font-mono text-[var(--ink-3)]">
                  {downstream.slug}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <span
                className={`rounded-full border-[0.5px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] ${
                  partnership.is_active
                    ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                    : 'border-[var(--status-error-border)] bg-[var(--status-error-bg)] text-[var(--status-error)]'
                }`}
              >
                {partnership.is_active ? 'Active' : 'Suspended'}
              </span>
              {partnership.role_name && (
                <span
                  className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]"
                >
                  {partnership.role_name}
                </span>
              )}
            </div>
          </div>
          {partnership.created_at && (
            <p className="text-[11px] text-[var(--ink-3)]">
              Added {formatDate(partnership.created_at)}
            </p>
          )}
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] p-4">
            <Handshake className="h-4 w-4 text-[var(--ink-3)]" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Upstream</p>
              <p className="text-[13px] text-[var(--ink)]">{partnership.upstream_organisation.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] p-4">
            <Users className="h-4 w-4 text-[var(--ink-3)]" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Members</p>
              <p className="text-[13px] text-[var(--ink)]">{members.length} ({usersWithAccess} with access)</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] p-4">
            <Building2 className="h-4 w-4 text-[var(--ink-3)]" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">User Management</p>
              <p className="text-[13px] text-[var(--ink)]">{STRATEGY_LABELS[partnership.user_management_strategy] ?? partnership.user_management_strategy}</p>
            </div>
          </div>
        </div>

        {/* Members - all org members with access indicator */}
        <section>
          <h3
            className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3"
          >
            <Users className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5 text-[var(--ink-3)]" />
            Members ({members.length})
          </h3>
          <div className="flex flex-col gap-1">
            {members.map((member) => (
              <div
                key={member.uuid}
                className="flex items-center justify-between px-3 py-2.5 rounded-[8px] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
              >
                <div className="min-w-0">
                  <p className="truncate text-[12px] text-[var(--ink)]">
                    {member.first_name} {member.last_name}
                  </p>
                  <p className="truncate text-[10px] text-[var(--ink-3)]">
                    {member.email}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <div className="flex flex-wrap gap-1">
                    {(member.role_names?.length
                      ? member.role_names
                      : member.role_name
                        ? [member.role_name]
                        : []
                    ).map((name) => (
                      <span
                        key={name}
                        className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                  <span
                    className={`rounded-full border-[0.5px] px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] ${
                      member.has_partnership_access
                        ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                        : 'border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]'
                    }`}
                  >
                    {member.has_partnership_access ? 'Access' : 'No access'}
                  </span>
                  {!member.is_staff && (
                    <button
                      title={`Impersonate ${member.email}`}
                      onClick={() => handleImpersonate(member.uuid)}
                      className="h-6 w-6 rounded-md flex items-center justify-center transition-colors duration-100 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--status-warn-bg)] hover:text-[var(--status-warn)]"
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {members.length === 0 && (
              <p className="px-3 py-4 text-[12px] text-[var(--ink-3)]">
                No members in this organisation yet.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
